<template>
    <div
        :class="classes"
    >
        <div
            class="kgr-chatMessage__inner kgr-postfix__fluid"
        >
            <div
                v-if="userHasLeft"
                class="kgr-text kgr-text--italic"
            >
                {{ $t('Der Verfasser ist nicht mehr in diesem Team') }}
            </div>
            <div
                v-else-if="displayMessage.deleted"
                class="kgr-text kgr-text--italic"
            >
                {{ $t('Diese Nachricht wurde als gelöscht markiert') }}
            </div>
            <div class="kgr-chatMessage__header">
                <div class="kgr-chatMessage__authorName">
                    {{ authorName }}
                </div>
                <kgr-icon
                    v-if="chat.userIsAllowedToManageMessages"
                    class="kgr-chatMessage__actionIcon"
                    icon-class="more-vertical"
                    tiny
                    @click="onClick()"
                />
            </div>
            <div class="kgr-chatMessage__content">
                <template v-if="loading">
                    <kgr-loading inline />
                </template>
                <template v-else>
                    {{ showTranslation && translateTo && displayMessage.translations?.text ? displayMessage.translations.text : displayMessage.text }}
                </template>
            </div>
            <div class="kgr-chatMessage__bottom kgr-text kgr-text--small">
                <span class="kgr-chatMessage__left">
                    <span
                        class="kgr-text kgr-text--underlined kgr-clickable"
                        @click="translateMessage"
                    >
                        {{ showTranslation && translateTo ? $t('Originaltext anzeigen') : $t('Übersetzung anzeigen') }}
                    </span>
                </span>
                <span
                    class="kgr-chatMessage__right"
                    :title="messageDate"
                >{{ relativeTime }}</span>
            </div>
            <div
                v-if="error"
                class="kgr-chatMessage__error"
            >
                {{ $t(error) }}
            </div>
        </div>
        <div
            v-show="select"
            class="kgr-chatMessage__actions kgr-postfix__fixed"
        >
            <button
                v-if="!displayMessage.deleted"
                class="kgr-button kgr-button--link kgr-button--danger"
                @click="update({deleted: true})"
            >
                <i class="icon-trash"></i>
                &nbsp;
                <span>{{ $t('delete') }}</span>
            </button>
            <button
                v-else
                class="kgr-button kgr-button--link kgr-button--info"
                @click="update({deleted: false})"
            >
                <i class="icon-trash-o"></i>
                &nbsp;
                <span>{{ $t('restore') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import { format, formatDistanceToNow } from '@legacy/js/date/date-fns.js'
import ChatEntry from '../../js/dto/ChatEntry.js';
import ChatMessage from '../../js/dto/ChatMessage.js';
import DateLocale from '../../js/date/DateLocale.js';
import Error from '../../js/model/Error.js';
import Icon from '../../components/common/icon/Icon.vue';
import Loading from '../../components/common/Loading.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-loading': Loading,
    },
    props: {
        chat: {
            type: ChatEntry,
            required: true,
        },
        institutionKey: {
            type: String,
            required: true,
        },
        message: {
            type: ChatMessage,
            required: true,
        },
        outgoing: {
            type: Boolean
        },
        translateTo: {
            type: String,
            required: false,
            default: null
        },
    },
    data() {
        return {
            loading: false,
            error: false,
            select: false,
            displayMessage: JSON.parse(JSON.stringify(this.message)),
            translatedText: null,
            showTranslation: this.message.translations?.text && this.translateTo && this.translateTo !== 'de_DE',
        }
    },
    computed: {
        classes() {
            let classes = ['kgr-row', 'kgr-chatMessage', 'animated fadeIn'];
            if (this.outgoing) {
                classes.push('is-outgoing');
            }
            if (this.displayMessage.deleted) {
                classes.push('is-deleted');
            }

            if (this.userHasLeft) {
                classes.push('has-author-left');
            } else if (this.userDeleted) {
                classes.push('has-author-deleted');
            } else {
                classes.push('is-segment-1');
            }

            if (this.error) {
                classes.push('has-error');
            }
            if (this.select) {
                classes.push('is-selected');
            }

            return classes.join(' ');
        },
        userHasLeft() {
            return this.displayMessage.authorStatus === 'left';
        },
        userDeleted() {
            return this.displayMessage.authorStatus === 'deleted';
        },
        authorName() {
            if (this.userDeleted) {
                return this.$t('Gelöschter Nutzer');
            }
            return this.displayMessage.authorName;
        },
        date() {
            return this.displayMessage?.update || this.displayMessage?.created;
        },
        messageDate() {
            return format(this.date, 'ppP', {locale: this.dateLocale});
        },
        relativeTime() {
            return formatDistanceToNow(this.date, {locale: this.dateLocale, addSuffix: true});
        },
        messageUpdateRoute() {
            switch (this.institutionKey) {
                case 'club':
                    return `/club/chat/${this.chat.id}/${this.displayMessage.id}/update/`;
                case 'kindergarten':
                    return `/chat/${this.chat.id}/${this.displayMessage.id}/update/`;
                default:
                    throw new Error(`Invalid institution key: ${this.institutionKey}`);
            }
        },
        messageTranslationRoute() {
            switch (this.institutionKey) {
                case 'club':
                    return `/club/chat/${this.chat.id}/translations`;
                case 'kindergarten':
                    return `/chat/${this.chat.id}/translations`;
                default:
                    throw new Error(`Invalid institution key: ${this.institutionKey}`);
            }
        },
        dateLocale() {
            return DateLocale.getLocale(this.translateTo);
        },
    },
    watch: {
        message: {
            deep: true,
            handler(newVal) {
                this.displayMessage = JSON.parse(JSON.stringify(newVal));
            }
        },
        translateTo: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal !== 'de_DE' && !this.displayMessage.translations?.text) {
                    this.requestTranlsation();
                }
            }
        }
    },
    methods: {
        onClick() {
            if (this.chat.userIsAllowedToManageMessages) {
                this.select = !this.select;
            }
        },
        update(params) {
            let backup = this.displayMessage;
            for (let k in params) {
                this.displayMessage[k] = params[k];
            }
            this.select = false;
            this.error = false;
            axios({
                url: this.messageUpdateRoute,
                method: 'patch',
                data: params
            })
                .catch(() => {
                    this.displayMessage = backup;
                    this.error = 'chat.error_generic'
                });
        },
        translateMessage() {
            if (this.showTranslation) {
                this.showTranslation = false;
                return;
            }
            if(this.displayMessage.translations?.text) {
                this.showTranslation = true;
                return;
            }
            this.requestTranlsation();
        },
        requestTranlsation() {
            this.loading = true;
            const params = {
                id: this.displayMessage.id,
                translationLanguage: this.translateTo,
            };
            axios({
                url: this.messageTranslationRoute,
                method: 'post',
                data: params
            }).then((response) => {
                this.displayMessage = new ChatMessage(response.data.data);
                this.showTranslation = true;
            }).catch(() => {
                this.error = 'Beim Übersetzen ist ein Fehler aufgetreten.'
            }).finally(() => {
                this.loading = false;
            })
            ;

        }
    },
}
</script>
<style lang="scss" scoped>
.is-selected .kgr-chatMessage__inner {
    background: rgb(220, 220, 220);
}
.is-selected:not(.is-outgoing) .kgr-chatMessage__inner::before {
    border-right: 10px solid rgb(220, 220, 220);
    border-top: 10px solid rgb(220, 220, 220);
}
.is-selected.is-outgoing .kgr-chatMessage__inner::before {
    border-left: 10px solid rgb(220, 220, 220);
    border-top: 10px solid rgb(220, 220, 220);
}
.kgr-chatMessage__bottom {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
}
.kgr-chatMessage__left {
    display: flex;
    align-items: center;
    gap: 6px;
}
.kgr-chatMessage__right {
    white-space: nowrap;
}
.kgr-chatMessage__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
   margin-bottom: 4px;
}
.kgr-chatMessage__authorName {
  flex-shrink: 1;
  margin-right: 8px;
}
.kgr-chatMessage__actionWrapper {
    position: absolute;
    top: 4px;
    right: 4px;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
